import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ChevronRight, MessageSquare, UserCheck, Mic, Shield, Clock, Sparkles,
  Phone, ClipboardList,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import DiscoveryCallDialog from "@/components/DiscoveryCallDialog";
import SEOHead from "@/components/SEOHead";
import AuthorByline from "@/components/AuthorByline";
import RelatedServices from "@/components/RelatedServices";
import { buildBreadcrumb, buildService, buildFAQ } from "@/lib/seo/schemas";

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, delay },
});

const journeyIcons = [Phone, ClipboardList, Sparkles];

const journey = [
  { title: "Discovery Call", desc: "We understand your institution's goals, student demographics, and placement expectations." },
  { title: "Customised Programme", desc: "Modules are tailored to your students' discipline, year, and career aspirations." },
  { title: "On-Campus Delivery", desc: "Interactive, high-energy sessions delivered on your campus with measurable outcomes." },
];

const STUDENTS_FAQS = [
  {
    q: "What does the campus-to-corporate programme cover?",
    a: "Corporate communication and professional etiquette, interview readiness and employability skills, presentation and public speaking, workplace behaviour, ethics and POSH awareness, time management and corporate mindset, plus confidence building and career clarity. Modules are combined to match the cohort rather than delivered as a fixed syllabus.",
  },
  {
    q: "Who is the student programme designed for?",
    a: "Final-year students and fresh graduates preparing for placements and their first corporate role. It suits cohorts who can write and study well but struggle with interview presence, workplace norms, speaking confidently in groups, and the shift from academic deadlines to professional accountability and ownership.",
  },
  {
    q: "How does an institution get started?",
    a: "Three steps. A discovery call to understand your goals, student demographics and placement expectations. A customised programme built around your students' discipline, year and career aspirations. Then interactive on-campus delivery, with outcomes agreed beforehand so the impact can be reviewed after the sessions.",
  },
  {
    q: "Are sessions delivered on campus or online?",
    a: "Campus-to-corporate workshops are usually delivered on site at the college or university as interactive, high-energy sessions. Blended or virtual cohorts are possible where scheduling, campus access or student location makes that easier for the institution to organise across multiple batches.",
  },
  {
    q: "How does interview readiness training work in practice?",
    a: "It is practice-led rather than lecture-based. Students work through real hiring scenarios covering interview presence, CV conversations and situational responses, then receive direct feedback. The aim is that a first real interview feels familiar, so nerves do not undo the preparation students have already done.",
  },
  {
    q: "What does it cost for a college or university?",
    a: "Programmes are quoted after the discovery call, since the investment depends on cohort size, number of modules, delivery days and location. Institutions receive a written proposal covering recommended modules, duration, delivery mode and investment before anything is confirmed or scheduled.",
  },
];

const ServicesStudents = () => (
  <div>
    <SEOHead
      title="Campus to Corporate Program Dubai — Student Training"
      description="Campus-to-corporate training and soft skills programs for students and graduates in Dubai. Build communication, employability and workplace readiness."
      schema={[
        buildService({
          name: "Campus to Corporate Soft Skills Training",
          description: "Workshops that prepare final-year students and fresh graduates for corporate life through communication, interview and workplace-etiquette training.",
          path: "/services/students",
          serviceType: "Campus to corporate training",
        }),
        buildBreadcrumb([
          { name: "Home", path: "/" },
          { name: "Services", path: "/services" },
          { name: "For Students", path: "/services/students" },
        ]),
        buildFAQ(STUDENTS_FAQS),
      ]}
    />

    {/* Hero */}
    <section className="bg-primary py-14 md:py-20">
      <div className="container mx-auto px-4 text-center">
        <nav className="mb-4 flex items-center justify-center gap-1.5 text-base text-primary-foreground/60">
          <Link to="/" className="transition-colors hover:text-primary-foreground">Home</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/services" className="transition-colors hover:text-primary-foreground">Services</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-primary-foreground">For Students</span>
        </nav>
        <motion.h1 {...fadeUp()} className="mx-auto max-w-4xl text-3xl font-heading font-bold text-primary-foreground md:text-5xl lg:text-6xl">
          Campus to Corporate Programmes
        </motion.h1>
        <motion.p {...fadeUp(0.1)} className="mx-auto mt-3 max-w-2xl font-sans text-lg text-primary-foreground/80">
          Preparing students for real-world careers with confidence, clarity & capability.
        </motion.p>
      </div>
    </section>

    {/* Who This Is For */}
    <section className="bg-background py-12">
      <div className="container mx-auto px-4">
        <motion.h2 {...fadeUp()} className="text-center text-3xl font-heading font-bold text-foreground md:text-5xl">Who This Is For</motion.h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { title: "Final-Year Students", desc: "Students preparing for campus placements and their first professional role." },
            { title: "Fresh Graduates", desc: "Graduates transitioning from academic life to the corporate environment." },
            { title: "Colleges & Universities", desc: "Institutions seeking industry-relevant soft skills interventions for students." },
            { title: "Training Institutions", desc: "Organisations preparing cohorts for employability and professional success." },
          ].map((item, i) => (
            <motion.div key={item.title} {...fadeUp(i * 0.08)} className="rounded-xl bg-card p-5 shadow-sm transition-shadow hover:shadow-md">
              <div className="flex h-11 w-11 items-center justify-center rounded-full bg-accent/15">
                <UserCheck className="h-5 w-5 text-accent" />
              </div>
              <h3 className="mt-3 text-lg font-heading font-bold text-foreground">{item.title}</h3>
              <p className="mt-1.5 font-sans text-base leading-relaxed text-muted-foreground">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>

    {/* Corporate Communication */}
    <section id="corporate-communication" className="bg-muted py-12">
      <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
        <motion.img
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          src="https://images.unsplash.com/photo-1522202176988-66273c2fd55f?w=600"
          alt="Corporate communication training for students"
          className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
          loading="lazy"
        />
        <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Corporate Communication & Professional Etiquette</h2>
          <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">NABET Soft Skills Trainer | City & Guilds Trainer</p>
          <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
            Students learn how to communicate effectively in professional environments — spoken, written, and behavioural. Focus areas include workplace etiquette, email writing, meetings, and corporate norms.
          </p>
          <ul className="mt-4 space-y-2.5">
            {["Professional communication confidence", "Workplace readiness", "Reduced culture shock in corporate environments"].map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <MessageSquare className="h-5 w-5 shrink-0 text-accent" />
                <span className="font-sans text-base text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>

    {/* Interview Readiness */}
    <section id="interview-readiness" className="bg-background py-12">
      <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Interview Readiness & Employability Skills</h2>
          <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">NABET | City & Guilds | ICF-Aligned Coaching</p>
          <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
            A highly practical programme covering interview presence, articulation of strengths, CV conversations, and situational responses using real hiring scenarios.
          </p>
          <ul className="mt-4 space-y-2.5">
            {["Clear articulation of skills and strengths", "Improved interview performance", "Higher placement confidence"].map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <UserCheck className="h-5 w-5 shrink-0 text-accent" />
                <span className="font-sans text-base text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.img
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?w=600"
          alt="Interview readiness training"
          className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
          loading="lazy"
        />
      </div>
    </section>

    {/* Presentation & Public Speaking */}
    <section id="public-speaking" className="bg-muted py-12">
      <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
        <motion.img
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          src="https://images.unsplash.com/photo-1475721027785-f74eccf877e2?w=600"
          alt="Public speaking and presentation skills"
          className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
          loading="lazy"
        />
        <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Presentation, Public Speaking & Elevator Pitch</h2>
          <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">City & Guilds | Image Consulting | Coaching</p>
          <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
            Students learn to speak with confidence, clarity, and structure — whether in interviews, presentations, or professional discussions.
          </p>
          <ul className="mt-4 space-y-2.5">
            {["Reduced fear of speaking", "Clear and impactful self-expression", "Strong first impressions"].map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <Mic className="h-5 w-5 shrink-0 text-accent" />
                <span className="font-sans text-base text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>

    {/* Workplace Behaviour & POSH */}
    <section id="workplace-posh" className="bg-background py-12">
      <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Workplace Behaviour, Ethics & POSH Awareness</h2>
          <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">Certified POSH Trainer | NABET</p>
          <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
            An essential module introducing students to workplace conduct, respect, inclusion, ethics, and boundaries — aligned with corporate compliance expectations.
          </p>
          <ul className="mt-4 space-y-2.5">
            {["Awareness of professional behaviour standards", "Respectful and inclusive workplace mindset", "Preparedness for corporate policies"].map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <Shield className="h-5 w-5 shrink-0 text-accent" />
                <span className="font-sans text-base text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.img
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          src="https://images.unsplash.com/photo-1552664730-d307ca884978?w=600"
          alt="Workplace behaviour and POSH awareness"
          className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
          loading="lazy"
        />
      </div>
    </section>

    {/* Time Management & Corporate Mindset */}
    <section id="time-management" className="bg-muted py-12">
      <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
        <motion.img
          initial={{ opacity: 0, x: -40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          src="https://images.unsplash.com/photo-1434030216411-0b793f4b4173?w=600"
          alt="Time management and corporate mindset training"
          className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
          loading="lazy"
        />
        <motion.div initial={{ opacity: 0, x: 40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Time Management, Decision-Making & Corporate Mindset</h2>
          <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">ICF-Aligned Coaching | Corporate Leadership Experience</p>
          <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
            Helps students shift from academic thinking to professional accountability — covering prioritisation, ownership, decision-making, and workplace expectations.
          </p>
          <ul className="mt-4 space-y-2.5">
            {["Improved self-management", "Stronger ownership mindset", "Readiness for performance-driven environments"].map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <Clock className="h-5 w-5 shrink-0 text-accent" />
                <span className="font-sans text-base text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
      </div>
    </section>

    {/* Confidence Building */}
    <section id="confidence" className="bg-background py-12">
      <div className="container mx-auto grid items-center gap-10 px-4 md:grid-cols-2">
        <motion.div initial={{ opacity: 0, x: -40 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }}>
          <h2 className="text-2xl font-heading font-bold text-foreground md:text-4xl">Confidence Building & Career Clarity</h2>
          <p className="mt-1.5 text-base font-semibold uppercase tracking-wide text-primary">ICF-Aligned Coach | ART Therapy Practitioner</p>
          <p className="mt-3 font-sans text-base leading-relaxed text-muted-foreground">
            A reflective and structured module addressing self-doubt, career confusion, and transition anxiety. Select creative and coaching tools are used contextually to build inner clarity.
          </p>
          <ul className="mt-4 space-y-2.5">
            {["Increased confidence", "Career clarity", "Emotional readiness for transition"].map((item) => (
              <li key={item} className="flex items-center gap-2.5">
                <Sparkles className="h-5 w-5 shrink-0 text-accent" />
                <span className="font-sans text-base text-muted-foreground">{item}</span>
              </li>
            ))}
          </ul>
        </motion.div>
        <motion.img
          initial={{ opacity: 0, x: 40 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          src="https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?w=600"
          alt="Confidence building and career clarity"
          className="h-64 w-full rounded-2xl object-cover shadow-lg md:h-80"
          loading="lazy"
        />
      </div>
    </section>

    {/* Journey */}
    <section className="bg-muted py-12">
      <div className="container mx-auto px-4 text-center">
        <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-foreground md:text-5xl">Your Journey With Us</motion.h2>
        <div className="mt-8 grid gap-6 md:grid-cols-3">
          {journey.map((item, i) => {
            const Icon = journeyIcons[i] ?? Phone;
            return (
              <motion.div key={item.title} {...fadeUp(i * 0.12)} className="relative rounded-xl bg-card p-6 text-center shadow-sm">
                <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-accent text-xl font-bold text-accent-foreground">{i + 1}</div>
                <Icon className="mx-auto mt-2.5 h-5 w-5 text-accent" />
                <h3 className="mt-2.5 text-lg font-heading font-bold text-foreground">{item.title}</h3>
                <p className="mt-1.5 font-sans text-base text-muted-foreground">{item.desc}</p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>

    {/* CTA */}
    <section className="bg-accent py-12">
      <div className="container mx-auto px-4 text-center">
        <motion.h2 {...fadeUp()} className="text-3xl font-heading font-bold text-accent-foreground md:text-5xl">Partner With Us</motion.h2>
        <motion.p {...fadeUp(0.1)} className="mx-auto mt-3 max-w-xl font-sans text-base text-accent-foreground/80">
          If you are a college, university, or training institution looking to prepare your students for placements and professional success, we'd love to design a customised Campus-to-Corporate intervention.
        </motion.p>
        <motion.div {...fadeUp(0.15)} className="mt-6 flex flex-wrap items-center justify-center gap-3">
          <DiscoveryCallDialog>
            <Button size="lg" className="bg-primary font-semibold text-primary-foreground hover:bg-primary/90">
              Book a Discovery Call
            </Button>
          </DiscoveryCallDialog>
          <Button asChild size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <Link to="/contact">Contact Us</Link>
          </Button>
        </motion.div>
      </div>
    </section>

    <section className="bg-background py-10">
      <div className="container mx-auto px-4">
        <AuthorByline />
      </div>
    </section>

    <RelatedServices current="students" />
  </div>
);

export default ServicesStudents;